import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, type ReactNode } from "react";

interface ConfettiPopProps {
  trigger: boolean;
  onComplete?: () => void;
}

interface Particle {
  id: number;
  x: number;
  y: number;
  rotation: number;
  color: string;
  scale: number;
}

const COLORS = ["#2563eb", "#ea580c", "#8b5cf6", "#4d7c0f", "#ef4444", "#facc15"];

function generateParticles(count: number): Particle[] {
  const particles: Particle[] = [];
  for (let i = 0; i < count; i++) {
    particles.push({
      id: i,
      x: (Math.random() - 0.5) * 160,
      y: (Math.random() - 0.5) * 120 - 40,
      rotation: Math.random() * 360,
      color: COLORS[i % COLORS.length] ?? COLORS[0] ?? "#2563eb",
      scale: 0.5 + Math.random() * 0.8,
    });
  }
  return particles;
}

export function ConfettiPop({ trigger, onComplete }: ConfettiPopProps): ReactNode {
  const [particles, setParticles] = useState<Particle[]>([]);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (trigger) {
      setParticles(generateParticles(12));
      setVisible(true);
      const timer = setTimeout(() => {
        setVisible(false);
        onComplete?.();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [trigger, onComplete]);

  return (
    <AnimatePresence>
      {visible && (
        <div className="confetti-container" aria-hidden="true">
          {particles.map((p) => (
            <motion.div
              key={p.id}
              className="confetti-particle"
              style={{ backgroundColor: p.color }}
              initial={{ x: 0, y: 0, scale: 0, rotate: 0, opacity: 1 }}
              animate={{
                x: p.x,
                y: p.y,
                scale: p.scale,
                rotate: p.rotation,
                opacity: 0,
              }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.8,
                ease: "easeOut",
              }}
            />
          ))}
        </div>
      )}
    </AnimatePresence>
  );
}
